import { Logger } from "shared/lib/Logger"
import { TreeNode } from "shared/types/TreeNode"

export function findChild<T>(
  id: string | null,
  children?: TreeNode<T>[] | null
): TreeNode<T> | null {
  if (!id || !children) return null
  let found: TreeNode<T> | null = null

  // eslint-disable-next-line prefer-const, no-restricted-syntax
  for (let child of children) {
    if (child.id === id) {
      found = child as TreeNode<T>
    } else {
      found = findChild(id, (child.children as TreeNode<T>[]) || [])
    }

    if (found) {
      return found
    }
  }
  return null
}

export function findChildren<T>(
  ids: string[],
  children?: TreeNode<T>[] | null
): TreeNode<T>[] {
  if (!ids || !children) return []
  const found: TreeNode<T>[] = []

  // eslint-disable-next-line prefer-const, no-restricted-syntax
  for (let child of children) {
    if (ids.includes(child.id)) {
      found.push(child)
    }
    found.push(...findChildren(ids, (child.children as TreeNode<T>[]) || []))
  }
  return found
}

export function findParent<T>(
  id: string | null,
  children?: TreeNode<T>[] | null
): T | null
export function findParent<T, P>(
  id: string | null,
  children?: TreeNode<T>[] | null,
  parent?: TreeNode<T> | P
): TreeNode<T> | P
export function findParent<T, P>(
  id: string | null,
  children?: TreeNode<T>[] | null,
  parent?: TreeNode<T> | P | null
): TreeNode<T> | P | null
export function findParent<T, P>(
  id: string | null,
  children?: TreeNode<T>[] | null,
  parent: TreeNode<T> | P | null = null
): TreeNode<T> | P | null {
  if (!id || !children) return null
  let found: TreeNode<T> | null = null

  if (children.some(child => child.id === id)) {
    return parent
  }
  // eslint-disable-next-line prefer-const, no-restricted-syntax
  for (let child of children) {
    if (child.children?.some(grandChild => grandChild.id === id)) {
      found = child as TreeNode<T>
    } else {
      found = findParent(id, child.children || [], child) as TreeNode<T> | null
    }

    if (found) {
      return found
    }
  }
  return null
}

export function confirmChildPosition<T>({
  id,
  expectedPositon,
  expectedParentID,
  children = null
}: {
  id: string | null
  expectedPositon: number
  expectedParentID: string | null
  children: TreeNode<T>[] | undefined | null
}): boolean {
  if (expectedParentID === null) {
    return children?.[expectedPositon]?.id === id
  }

  const parent = findParent(id, children, null)
  if (parent) {
    return (
      parent?.id === expectedParentID &&
      parent.children?.[expectedPositon]?.id === id
    )
  }

  return false
}

export function findParents<T>(
  id: string | null,
  children?: TreeNode<T>[]
): T[] | null
export function findParents<T>(
  id: string | null,
  children?: TreeNode<T>[] | null,
  parents?: TreeNode<T>[]
): TreeNode<T>[] | null
export function findParents<T>(
  id: string | null,
  children?: TreeNode<T>[] | null,
  parents: TreeNode<T>[] = []
): TreeNode<T>[] | null {
  if (!id || !children) return null
  // eslint-disable-next-line no-restricted-syntax
  for (const child of children) {
    if (child.id === id) {
      return parents.length > 0 ? parents : null
    }

    if (child.children) {
      const result = findParents(id, child.children, [...parents, child])
      if (result !== null) {
        return result
      }
    }
  }

  return null
}

/**
 * Finds all ancestors of a node with the given ID in a tree structure.
 * Returns an array of ancestors from the immediate parent up to the root.
 *
 * @param id - The ID of the node to find ancestors for
 * @param children - The tree of children to search through
 * @param includeChild - If true, includes the child itself as the first element in the returned array
 * @returns Array of ancestor data (T[]) when called without ancestors parameter, or null if not found
 */
export function findAncestors<T>(
  id: string | null,
  children?: TreeNode<T>[],
  includeChild?: boolean
): T[] | null
/**
 * Finds all ancestors of a node with the given ID in a tree structure.
 * Returns an array of ancestor TreeNodes from the immediate parent up to the root.
 *
 * @param id - The ID of the node to find ancestors for
 * @param children - The tree of children to search through
 * @param includeChild - If true, includes the child itself as the first element in the returned array
 * @param ancestors - Accumulator array for building up the ancestor chain
 * @returns Array of ancestor TreeNodes when called with ancestors parameter, or null if not found
 */
export function findAncestors<T>(
  id: string | null,
  children?: TreeNode<T>[] | null,
  includeChild?: boolean,
  ancestors?: TreeNode<T>[]
): TreeNode<T>[] | null
export function findAncestors<T>(
  id: string | null,
  children?: TreeNode<T>[] | null,
  includeChild = false,
  ancestors: TreeNode<T>[] = []
): TreeNode<T>[] | null {
  if (!id || !children) return null
  // eslint-disable-next-line no-restricted-syntax
  for (const child of children) {
    if (child.id === id) {
      if (ancestors.length === 0 && !includeChild) return null
      const orderedAncestors = [...ancestors].reverse()
      if (includeChild) {
        return [child, ...orderedAncestors]
      }
      return orderedAncestors
    }

    if (child.children) {
      const result = findAncestors(id, child.children, includeChild, [
        ...ancestors,
        child
      ])
      if (result !== null) {
        return result
      }
    }
  }

  return null
}

export function filterTree<T>(
  children: null | undefined,
  predicate: (node: TreeNode<T>) => boolean
): null
export function filterTree<T>(
  children: TreeNode<T>[],
  predicate: (node: TreeNode<T>) => boolean
): T[]
export function filterTree<T>(
  children: TreeNode<T>[] | null | undefined,
  predicate: (node: TreeNode<T>) => boolean
): T[] | null
export function filterTree<T>(
  children: TreeNode<T>[] | null | undefined,
  predicate: (node: TreeNode<T>) => boolean
): T[] | null {
  return (
    children?.filter(predicate).map(child => ({
      ...child,
      children: filterTree(child.children, predicate)
    })) || null
  )
}

export function reduceTree<T, S>(
  children: TreeNode<T>[] | null | undefined,
  reduceFn: (current: S, node: TreeNode<T>) => S,
  initalValue: S
): S {
  return (
    children?.reduce(
      (current: S, child: TreeNode<T>) =>
        reduceTree(child.children, reduceFn, current),
      children?.reduce(reduceFn, initalValue) || initalValue
    ) || initalValue
  )
}

export function forEachNode<T>(
  children: TreeNode<T>[] | null | undefined,
  fn: (node: TreeNode<T>) => void
) {
  children?.forEach((child: TreeNode<T>) => {
    fn(child)
    if (child.children) {
      forEachNode(child.children, fn)
    }
  })
}

export function mapTree<T>(
  children: TreeNode<T>[] | null | undefined,
  fn: (node: TreeNode<T>) => TreeNode<T>
): TreeNode<T>[] | null | undefined {
  if (!children) return children

  // Evicted or partially resolved Apollo cache references can surface as
  // nullish entries; mapping them would throw during render.
  let skippedCount = 0
  const mapped = children.reduce<TreeNode<T>[]>((acc, child) => {
    if (!child) {
      skippedCount += 1
      return acc
    }
    const mappedChild = fn(child)
    acc.push(
      mappedChild.children
        ? { ...mappedChild, children: mapTree(mappedChild.children, fn) }
        : mappedChild
    )
    return acc
  }, [])

  if (skippedCount) {
    Logger.warn(
      new Error("mapTree skipped nullish children"),
      "Nullish child in tree",
      { skippedCount, totalCount: children.length }
    )
  }

  return mapped
}
export function flattenTree(children: null): null
export function flattenTree(children: undefined): undefined
export function flattenTree<T>(children: TreeNode<T>[]): TreeNode<T>[]
export function flattenTree<T>(
  children: TreeNode<T>[] | null | undefined
): TreeNode<T>[] | null | undefined
export function flattenTree<T>(
  children: TreeNode<T>[] | null | undefined
): TreeNode<T>[] | null | undefined {
  return children?.flatMap((child: TreeNode<T>) => [
    child,
    ...(flattenTree(child.children) || [])
  ])
}
